import { useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { AlertTriangle, Plus, RotateCcw, Save, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useDocumentOutline } from "../api/pdfApi";
import type { PdfDocument, PdfOfficeOutline, PdfOfficeTable } from "../types";

/**
 * Treść pliku biurowego — do poprawiania wprost, bez objazdu przez PDF-a.
 *
 * Różnica wobec edytora PDF-a jest zasadnicza: tam poprawka to praca na obrazie
 * strony (zmierz wcięcie, dopasuj krój, zamaluj stare), tu akapit i wiersz tabeli
 * są w pliku zapisane wprost. Dlatego ten ekran nie ma ani jednego suwaka
 * i ani jednej współrzędnej — jest listą tego, co w dokumencie jest, i pola do
 * wpisania tego, co ma być.
 *
 * Dwie ścieżki zapisu, bo backend traktuje je inaczej:
 *
 * * POPRAWKI treści nie ruszają układu dokumentu, więc zbierają się i idą JEDNYM
 *   wywołaniem („Zapisz zmiany”) — jedna nowa wersja pliku zamiast dziesięciu.
 * * DOPISANIE akapitu albo wiersza przesuwa identyfikatory wszystkiego, co jest
 *   pod spodem. Dlatego wykonuje się od razu i pojedynczo, a przed nim wypychamy
 *   zaległe poprawki — inaczej zapisalibyśmy je pod adresami sprzed przesunięcia.
 */

/** Zmiany do wysłania: tylko te pola, które naprawdę różnią się od dokumentu. */
export function pendingEdits(
  drafts: Record<string, string>,
  original: Record<string, string>,
): { id: string; text: string }[] {
  return Object.entries(drafts)
    .filter(([id, text]) => text !== (original[id] ?? ""))
    .map(([id, text]) => ({ id, text }));
}

/** Adresy komórek i akapitów — te same, którymi posługuje się backend i asystent. */
export function cellId(tableId: string, row: number, column: number): string {
  return `${tableId}:${row}:${column}`;
}

export function sheetCellId(sheet: string, column: string, row: number): string {
  return `${sheet}!${column}${row}`;
}

/** Bieżąca treść wszystkiego, co da się poprawić — po adresach. */
export function outlineValues(outline: PdfOfficeOutline): Record<string, string> {
  const out: Record<string, string> = {};
  outline.blocks.forEach((block) => {
    if (block.type === "paragraph") out[block.id] = block.text;
    else
      block.rows.forEach((row, r) =>
        row.forEach((value, c) => {
          out[cellId(block.id, r, c)] = value;
        }),
      );
  });
  outline.sheets.forEach((sheet) =>
    sheet.rows.forEach((row) =>
      row.cells.forEach((value, index) => {
        out[sheetCellId(sheet.name, sheet.columns[index] ?? "", row.row)] = value;
      }),
    ),
  );
  return out;
}

/** Pole tekstowe rosnące razem z treścią — akapit ma być widoczny w całości. */
function GrowingText({
  value,
  onChange,
  className,
  ariaLabel,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      aria-label={ariaLabel}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={clsx(
        "w-full resize-none border border-transparent bg-transparent px-1.5 py-1 text-xs leading-snug text-[var(--color-text-primary)] outline-none",
        "hover:border-[var(--color-border)] focus:border-[var(--color-primary)] focus:bg-[var(--color-bg-surface)]",
        "disabled:cursor-not-allowed disabled:opacity-60",
        className,
      )}
    />
  );
}

function StyleBadge({ style, level }: { style: string; level: number }) {
  if (!style && !level) return null;
  return (
    <span
      title={style ? `Styl dokumentu: ${style}` : undefined}
      className="mt-1 shrink-0 select-none font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]"
    >
      {style || `H${level}`}
    </span>
  );
}

export function OfficeEditor({
  document: doc,
  onApply,
  saving,
}: {
  document: PdfDocument;
  /** Wykonanie narzędzia na tym dokumencie; w wyniku „czy się udało”. Dopisywanie
   * musi poczekać na wypchnięcie zaległych poprawek — i nie może iść dalej, gdy
   * to wypchnięcie padło, bo wyrzuciłoby niezapisaną pracę człowieka. */
  onApply: (operation: string, params: Record<string, unknown>) => Promise<boolean>;
  saving: boolean;
}) {
  const { data: outline, isLoading, error } = useDocumentOutline(doc.id, doc.version_number);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [addAfter, setAddAfter] = useState<string | null>(null);
  const [draftParagraph, setDraftParagraph] = useState("");
  const [draftRow, setDraftRow] = useState<Record<string, string[]>>({});

  const original = useMemo(() => (outline ? outlineValues(outline) : {}), [outline]);

  // Nowa wersja pliku = nowy konspekt i nowe adresy. Cokolwiek zostało w polach,
  // dotyczyło poprzedniego układu dokumentu — trzymanie tego byłoby zapisem
  // pod przesuniętym adresem.
  useEffect(() => {
    setDrafts({});
    setAddAfter(null);
    setDraftParagraph("");
    setDraftRow({});
  }, [doc.id, doc.version_number]);

  const readOnly = outline?.read_only ?? "";
  const pending = pendingEdits(drafts, original);
  const busy = saving;

  const value = (id: string) => drafts[id] ?? original[id] ?? "";
  const set = (id: string, text: string) => setDrafts((d) => ({ ...d, [id]: text }));

  const flush = async (): Promise<boolean> => {
    if (pending.length === 0) return true;
    const done = await onApply("office_edit_text", { edits: pending });
    if (done) setDrafts({});
    return done;
  };

  const addParagraph = async (after: string) => {
    const text = draftParagraph.trim();
    if (!text) return;
    if (!(await flush())) return;
    const done = await onApply("office_add_paragraph", {
      after,
      paragraphs: text.split("\n").filter((line) => line.trim()),
    });
    if (!done) return;
    setAddAfter(null);
    setDraftParagraph("");
  };

  const addRow = async (table: string, cells: string[]) => {
    if (cells.every((c) => !c.trim())) return;
    if (!(await flush())) return;
    if (await onApply("office_add_row", { table, cells })) {
      setDraftRow((d) => ({ ...d, [table]: [] }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (error || !outline) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-xs text-[var(--color-text-muted)]">
        Nie udało się odczytać treści tego dokumentu.
      </div>
    );
  }

  const rowDraft = (table: string, columns: number) =>
    draftRow[table] ?? Array.from({ length: columns }, () => "");

  const setRowCell = (table: string, columns: number, index: number, text: string) =>
    setDraftRow((d) => {
      const next = [...(d[table] ?? Array.from({ length: columns }, () => ""))];
      next[index] = text;
      return { ...d, [table]: next };
    });

  // Uwaga: `insertSlot` i `renderTable` są ZWYKŁYMI funkcjami, a nie komponentami
  // zagnieżdżonymi w tym komponencie. Komponent zdefiniowany w ciele renderowania
  // ma przy każdym renderze nową tożsamość, więc React montuje go od nowa — a pole
  // tekstowe traci wtedy kursor po każdej wpisanej literze.
  /** Miejsce na nowy akapit — po wskazanym bloku. */
  const insertSlot = (after: string) =>
    addAfter === after ? (
      <div className="my-1 border border-[var(--color-primary)] bg-[var(--color-bg-surface)] p-2">
        <GrowingText
          value={draftParagraph}
          onChange={setDraftParagraph}
          ariaLabel="Treść nowego akapitu"
          className="border-[var(--color-border)]"
        />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-[11px] text-[var(--color-text-muted)]">
            Styl weźmie się z akapitu powyżej — piszesz samą treść.
          </span>
          <span className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setAddAfter(null);
                setDraftParagraph("");
              }}
            >
              <X size={13} className="mr-1" /> Anuluj
            </Button>
            <Button
              type="button"
              size="sm"
              loading={busy}
              disabled={!draftParagraph.trim()}
              onClick={() => addParagraph(after)}
            >
              Dopisz
            </Button>
          </span>
        </div>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => {
          setAddAfter(after);
          setDraftParagraph("");
        }}
        aria-label={`Dopisz akapit po bloku ${after}`}
        title="Dopisz akapit w tym miejscu"
        className="group flex h-3 w-full items-center justify-center opacity-0 transition-opacity duration-100 hover:opacity-100 focus:opacity-100"
      >
        <span className="h-px flex-1 bg-[var(--color-primary)]" />
        <Plus size={12} className="mx-1 shrink-0 text-[var(--color-primary)]" />
        <span className="h-px flex-1 bg-[var(--color-primary)]" />
      </button>
    );

  const renderTable = (block: PdfOfficeTable) => {
    const columns = block.columns || (block.rows[0]?.length ?? 1);
    const draft = rowDraft(block.id, columns);
    return (
      <div className="my-2 overflow-x-auto">
        <table className="w-full min-w-[24rem] border-collapse text-xs">
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={`${block.id}-${r}`}>
                {row.map((_cell, c) => (
                  <td
                    key={`${block.id}-${r}-${c}`}
                    className="border border-[var(--color-border)] p-0 align-top"
                  >
                    <GrowingText
                      value={value(cellId(block.id, r, c))}
                      onChange={(v) => set(cellId(block.id, r, c), v)}
                      ariaLabel={`Komórka ${cellId(block.id, r, c)}`}
                      disabled={!!readOnly}
                    />
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              {draft.map((cell, index) => (
                <td
                  key={`${block.id}-new-${index}`}
                  className="border border-dashed border-[var(--color-border-strong)] p-0 align-top"
                >
                  <input
                    type="text"
                    value={cell}
                    aria-label={`Nowy wiersz, kolumna ${index + 1}`}
                    onChange={(e) => setRowCell(block.id, columns, index, e.target.value)}
                    className="w-full bg-transparent px-1.5 py-1 text-xs text-[var(--color-text-primary)] outline-none focus:bg-[var(--color-bg-surface)]"
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            {block.id} · {block.rows_total} w. × {columns} kol.
            {block.rows.length < block.rows_total && " · pokazano początek"}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={busy}
            disabled={draft.every((c) => !c.trim())}
            onClick={() => addRow(block.id, draft)}
          >
            <Plus size={13} className="mr-1" /> Dopisz wiersz
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-3 py-1.5">
        <span className="truncate font-mono text-[11px] text-[var(--color-text-secondary)]">
          {doc.name} · wersja {doc.version_number} · {outline.kind.toUpperCase()}
        </span>
        <span className="flex items-center gap-2">
          {pending.length > 0 && (
            <>
              <span className="text-[11px] text-[var(--color-text-muted)]">
                zmienione: {pending.length}
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setDrafts({})}>
                <RotateCcw size={13} className="mr-1" /> Odrzuć
              </Button>
            </>
          )}
          <Button
            type="button"
            size="sm"
            loading={busy}
            disabled={pending.length === 0 || !!readOnly}
            onClick={flush}
          >
            <Save size={13} className="mr-1.5" /> Zapisz zmiany
          </Button>
        </span>
      </div>

      {readOnly && (
        <p className="flex items-start gap-1.5 border-b border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-3 py-1.5 text-[11px] text-[var(--color-text-primary)]">
          <AlertTriangle size={13} className="mt-px shrink-0 text-[var(--color-primary)]" />
          <span>
            Tego skoroszytu nie zapiszę — zawiera: {readOnly}. Zapis skasowałby te
            elementy, więc treść jest tu tylko do czytania. Popraw plik w Excelu albo
            zrób z niego PDF-a.
          </span>
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {outline.blocks.length === 0 && outline.sheets.length === 0 && (
          <p className="py-6 text-center text-xs text-[var(--color-text-muted)]">
            Ten dokument nie ma treści, którą dałoby się tu poprawić.
          </p>
        )}

        {outline.blocks.map((block) =>
          block.type === "paragraph" ? (
            <div key={block.id}>
              <div className="flex items-start gap-2">
                <StyleBadge style={block.style} level={block.level} />
                <GrowingText
                  value={value(block.id)}
                  onChange={(v) => set(block.id, v)}
                  ariaLabel={`Akapit ${block.id}`}
                  disabled={!!readOnly}
                  className={clsx(
                    block.level === 1 && "text-sm font-semibold",
                    block.level >= 2 && "font-medium",
                  )}
                />
              </div>
              {insertSlot(block.id)}
            </div>
          ) : (
            <div key={block.id}>
              {renderTable(block)}
              {insertSlot(block.id)}
            </div>
          ),
        )}

        {outline.sheets.map((sheet) => {
          const draft = rowDraft(sheet.name, sheet.columns.length);
          return (
            <section key={sheet.id} className="mb-4">
              <h3 className="section-label mb-1">
                {sheet.name} · {sheet.rows_total} w. × {sheet.columns_total} kol.
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[24rem] border-collapse text-xs">
                  <thead>
                    <tr>
                      {sheet.columns.map((column) => (
                        <th
                          key={column}
                          className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-left font-mono text-[10px] font-normal text-[var(--color-text-muted)]"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.rows.map((row) => (
                      <tr key={`${sheet.id}-${row.row}`}>
                        {row.cells.map((_cell, index) => {
                          const id = sheetCellId(sheet.name, sheet.columns[index] ?? "", row.row);
                          return (
                            <td
                              key={id}
                              className="border border-[var(--color-border)] p-0 align-top"
                            >
                              <GrowingText
                                value={value(id)}
                                onChange={(v) => set(id, v)}
                                ariaLabel={`Komórka ${id}`}
                                disabled={!!readOnly}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {!readOnly && (
                      <tr>
                        {sheet.columns.map((column, index) => (
                          <td
                            key={`${sheet.id}-new-${column}`}
                            className="border border-dashed border-[var(--color-border-strong)] p-0 align-top"
                          >
                            <input
                              type="text"
                              value={draft[index] ?? ""}
                              aria-label={`Nowy wiersz arkusza ${sheet.name}, kolumna ${column}`}
                              onChange={(e) =>
                                setRowCell(sheet.name, sheet.columns.length, index, e.target.value)
                              }
                              className="w-full bg-transparent px-1.5 py-1 text-xs text-[var(--color-text-primary)] outline-none focus:bg-[var(--color-bg-surface)]"
                            />
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {!readOnly && (
                <div className="mt-1 flex justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={busy}
                    disabled={draft.every((c) => !c.trim())}
                    onClick={() => addRow(sheet.name, draft)}
                  >
                    <Plus size={13} className="mr-1" /> Dopisz wiersz
                  </Button>
                </div>
              )}
            </section>
          );
        })}

        {outline.blocks.length > 0 && !readOnly && (
          <div className="mt-2 border-t border-dashed border-[var(--color-border)] pt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setAddAfter("");
                setDraftParagraph("");
              }}
            >
              <Plus size={13} className="mr-1" /> Dopisz akapit na końcu
            </Button>
            {addAfter === "" && (
              <div className="mt-2 border border-[var(--color-primary)] bg-[var(--color-bg-surface)] p-2">
                <GrowingText
                  value={draftParagraph}
                  onChange={setDraftParagraph}
                  ariaLabel="Treść nowego akapitu na końcu"
                  className="border-[var(--color-border)]"
                />
                <div className="mt-1.5 flex justify-end gap-1.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAddAfter(null);
                      setDraftParagraph("");
                    }}
                  >
                    Anuluj
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    loading={busy}
                    disabled={!draftParagraph.trim()}
                    onClick={() => addParagraph("")}
                  >
                    Dopisz
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {outline.truncated && (
          <p className="mt-3 text-[11px] text-[var(--color-text-muted)]">
            Dokument jest długi — pokazujemy jego początek. Reszta jest w pliku
            nietknięta.
          </p>
        )}
      </div>
    </div>
  );
}
